/** Single source of truth for blog posts: blog index cards, sitemaps and Article schema. */
export const SITE = "https://zipgif.com";
export const AUTHOR = { name: "Shafiullah Tareen", url: `${SITE}/about` };

export type BlogPost = {
  path: string;
  title: string;
  description: string;
  excerpt: string;
  published: string; // ISO date
  updated?: string; // ISO date, when the article was last revised
  date: string; // display date
  read: string;
  tag: string;
  image: string; // public path, 1200×630-ish cover
  alt: string;
};

export const BLOG_POSTS: BlogPost[] = [
  {
    path: "/blog/how-many-fps-are-gifs",
    title: "How Many FPS Are GIFs? Frame Delays Explained",
    description:
      "Learn how GIF frame delays relate to FPS, calculate approximate animation speed, and understand why playback can vary between viewers.",
    excerpt:
      "A GIF has no single frame rate: each frame carries its own delay. How to convert delays to nominal FPS, why variable timing breaks the average, and why viewers can disagree.",
    published: "2026-10-24",
    date: "October 24, 2026",
    read: "9 min read",
    tag: "GIF frame rate",
    image: "/og-how-many-fps-are-gifs.svg",
    alt: "How many FPS are GIFs: GIF frame delays and nominal frame rate explained by ZipGIF",
  },
  {
    path: "/blog/how-to-resize-a-gif",
    title: "How to Resize a GIF",
    description:
      "Learn how to resize a GIF by exact pixels, percentage, or platform preset, keep the aspect ratio, and understand how dimensions relate to file size.",
    excerpt:
      "Halving both sides quarters the pixel area, the aspect lock is what keeps a GIF from stretching, and a pixel number means resize while a KB number means compress.",
    published: "2026-10-26",
    updated: "2026-10-26",
    date: "October 26, 2026",
    read: "9 min read",
    tag: "GIF size",
    image: "/og-how-to-resize-a-gif.svg",
    alt: "How to resize a GIF without stretching it: an 800 by 600 GIF becoming 400 by 300, a quarter of the pixel area",
  },
  {
    path: "/blog/why-is-my-gif-so-big",
    title: "Why Is My GIF So Big? 7 Causes and How to Fix Each",
    description:
      "Find out why your GIF file is so big, from dimensions and frame count to noise and colours, and which fix shrinks it fastest without guesswork.",
    excerpt:
      "Dimensions, frame count, colours, noise and missing optimization: what actually makes a GIF heavy, and the fix that matches each cause.",
    published: "2026-10-07",
    date: "October 7, 2026",
    read: "8 min read",
    tag: "GIF size",
    image: "/og-why-is-my-gif-so-big.jpg",
    alt: "Why is my GIF so big: a large GIF file shrinking into a smaller optimized GIF with fewer colours and pixels",
  },
  {
    path: "/blog/how-to-crop-a-gif",
    title: "How to Crop a GIF",
    description:
      "Learn how to crop a GIF without losing the animation: crop vs resize vs trim, aspect ratios, what happens to frames and file size, and the mistakes to avoid.",
    excerpt:
      "Cropping cuts the same rectangle out of every frame, so the picture shrinks and the animation stays. Which job it is, which preset fits, and why bytes fall more slowly than pixels.",
    published: "2026-10-26",
    date: "October 26, 2026",
    read: "10 min read",
    tag: "GIF cropping",
    image: "/og-how-to-crop-a-gif.svg",
    alt: "How to crop a GIF without losing the motion: crop vs resize vs trim, framing, and file size explained by ZipGIF",
  },
  {
    path: "/blog/how-to-make-a-gif-into-a-sticker",
    title: "How to Turn a GIF Into a Sticker: Static vs. Animated",
    description:
      "Learn how to turn GIF artwork into a sticker, understand static versus animated formats, and check your destination platform's requirements.",
    excerpt:
      "A GIF is not automatically a sticker: Telegram needs PNG, WebP, WebM or vector .TGS, while Discord accepts GIFs up to 512 KiB. Pick the platform, then the format.",
    published: "2026-10-24",
    updated: "2026-10-26",
    date: "October 24, 2026",
    read: "12 min read",
    tag: "Stickers",
    image: "/og-how-to-make-a-gif-into-a-sticker.svg",
    alt: "How to turn a GIF into a sticker: static, animated, and video sticker paths explained by ZipGIF",
  },
  {
    path: "/blog/how-to-make-a-gif-from-video",
    title: "How to Make a GIF From a Video",
    description:
      "Learn how to make a GIF from a video, whether with a direct converter or by extracting frames and building the GIF with ZipGIF's image-to-GIF tool.",
    excerpt:
      "ZipGIF has no video converter, so this guide is honest about that: use a direct converter for one-step conversion, or extract frames and build the GIF privately in your browser.",
    published: "2026-10-26",
    updated: "2026-10-26",
    date: "October 26, 2026",
    read: "11 min read",
    tag: "Video to GIF",
    image: "/og-how-to-make-a-gif-from-video.svg",
    alt: "How to make a GIF from a video: a video clip becoming a looping GIF, explained by ZipGIF",
  },
  {
    path: "/blog/how-long-can-a-gif-be",
    title: "How Long Can a GIF Be? Duration, Loops and Upload Limits",
    description:
      "Learn how long a GIF can be, how frame delays and loops affect duration, and when trimming, compression or video is the better choice.",
    excerpt:
      "How frame delays add up, why a short GIF can loop forever, and what to do when a GIF is too long or too large to upload.",
    published: "2026-10-03",
    date: "October 3, 2026",
    read: "9 min read",
    tag: "GIF length",
    image: "/og-how-long-can-a-gif-be.jpg",
    alt: "How long can a GIF be: GIF duration, frame delay and loop count explained by ZipGIF",
  },
  {
    path: "/blog/best-free-gif-maker",
    title: "What Is the Best Free GIF Maker?",
    description:
      "Compare free GIF makers on price, watermarks, signups, privacy, video import and editing — and find which one fits what you actually want to make.",
    excerpt:
      "No single maker wins everything: GIPHY and EZGIF handle video, Canva and Imgflip handle text and design, ZipGIF keeps your files on your device. Pick by your starting point.",
    published: "2026-10-26",
    updated: "2026-10-26",
    date: "October 26, 2026",
    read: "10 min read",
    tag: "GIF makers",
    image: "/og-best-free-gif-maker.svg",
    alt: "What is the best free GIF maker: comparing GIPHY, EZGIF, Canva, Imgflip and ZipGIF by price, watermark, privacy and features",
  },
  {
    path: "/blog/how-to-slow-down-a-gif",
    title: "How to Slow Down a GIF Without Making It Choppy",
    description:
      "Learn how to slow down a GIF, adjust frame timing, avoid choppy playback, and choose the right method for your animation.",
    excerpt:
      "Scaling frame delays, duplicating frames, and deleting frames do very different things — and only the first two actually slow an animation down.",
    published: "2026-10-24",
    date: "October 24, 2026",
    read: "10 min read",
    tag: "GIF speed",
    image: "/og-how-to-slow-down-a-gif.svg",
    alt: "How to slow down a GIF without making it choppy: frame timing, methods, and trade-offs explained by ZipGIF",
  },
  {
    path: "/blog/how-to-animate-a-png",
    title: "How to Animate a PNG Image: APNG, GIF, and Frame Sequences",
    description:
      "Learn how to animate a PNG image, understand APNG versus GIF, and choose the right workflow for creating an animated image.",
    excerpt:
      "A still PNG holds one image, so motion must be created or converted: build frames, order a sequence, or translate an APNG into GIF.",
    published: "2026-10-24",
    updated: "2026-10-26",
    date: "October 24, 2026",
    read: "13 min read",
    tag: "PNG animation",
    image: "/og-how-to-animate-a-png.svg",
    alt: "How to animate a PNG image: one still PNG frame becoming a row of animation frames, explained by ZipGIF",
  },
  {
    path: "/blog/how-to-make-a-gif-size-smaller",
    title: "How to Make a GIF Smaller Without Ruining Quality",
    description:
      "Learn how to reduce GIF file size by adjusting dimensions, frames, duration, and image detail while choosing the best quality trade-offs.",
    excerpt:
      "Five ways to shrink a GIF — trimming, resizing, fewer frames, palette and compression — how to pick the right one for your file, and what a controlled same-file test actually showed.",
    published: "2026-10-24",
    updated: "2026-10-26",
    date: "October 24, 2026",
    read: "10 min read",
    tag: "GIF size",
    image: "/og-how-to-make-a-gif-size-smaller.svg",
    alt: "How to make a GIF smaller without ruining quality: a large GIF file shrinking into a smaller one by trimming, resizing, dropping frames and compressing",
  },
  {
    path: "/blog/how-to-make-a-gif-from-images",
    title: "How to Make a GIF From Images",
    description:
      "Learn how to make a GIF from images: pick and order PNG, JPG, or WebP frames, set the delay, choose looping, and export — all in your browser.",
    excerpt:
      "A GIF from images is a stack of stills played in order: pick the frames, drag them into order, set one delay for all of them, and export.",
    published: "2026-10-26",
    updated: "2026-10-26",
    date: "October 26, 2026",
    read: "10 min read",
    tag: "GIF from images",
    image: "/og-how-to-make-a-gif-from-images.svg",
    alt: "How to make a GIF from images: three still images becoming one animated GIF, explained by ZipGIF",
  },
];

export function getPost(path: string): BlogPost {
  const p = BLOG_POSTS.find((x) => x.path === path);
  if (!p) throw new Error(`Unknown blog post ${path}`);
  return p;
}

/** Head config for a blog post: meta, canonical, Article + BreadcrumbList JSON-LD. */
export function blogPostHead(post: BlogPost, extraImages: string[] = []) {
  const url = `${SITE}${post.path}`;
  const image = `${SITE}${post.image}`;
  return {
    meta: [
      { title: post.title },
      { name: "description", content: post.description },
      { name: "author", content: AUTHOR.name },
      { property: "og:title", content: post.title },
      { property: "og:description", content: post.description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:site_name", content: "ZipGIF" },
      { property: "og:image", content: image },
      { property: "og:image:alt", content: post.alt },
      { property: "article:published_time", content: post.published },
      { property: "article:author", content: AUTHOR.url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: post.title },
      { name: "twitter:description", content: post.description },
      { name: "twitter:image", content: image },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.description,
          datePublished: post.published,
          dateModified: post.updated ?? post.published,
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          image: [image, ...extraImages.map((i) => `${SITE}${i}`)],
          author: { "@type": "Person", name: AUTHOR.name, url: AUTHOR.url },
          publisher: {
            "@type": "Organization",
            name: "ZipGIF",
            url: SITE,
            logo: { "@type": "ImageObject", url: `${SITE}/icon-512.png` },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
            { "@type": "ListItem", position: 3, name: post.title, item: url },
          ],
        }),
      },
    ],
  };
}
